import { useTranslation } from 'react-i18next';
import { type CalculationResults, type CropConfig, type WeightUnit, WEIGHT_FACTORS, WEIGHT_LABELS } from './types';
import { formatNumber, formatCurrency } from './utils';

interface Props {
  results: CalculationResults;
  crops: CropConfig[];
  currencyCode: string;
  weightUnit: WeightUnit;
}

export function ResultsSummary({ results, crops, currencyCode, weightUnit }: Props) {
  const { t } = useTranslation();
  const wf = WEIGHT_FACTORS[weightUnit];
  const wLabel = WEIGHT_LABELS[weightUnit];

  return (
    <div className="card card-dark !p-4 md:!p-5">
      <h3 className="text-base font-display mb-3 text-center">{t('summary.title')}</h3>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/20">
              <th className="text-left py-2 px-3 font-semibold">{t('summary.crop')}</th>
              <th className="text-right py-2 px-3 font-semibold">{t('summary.demoFarmWeight', { weight: wLabel })}</th>
              <th className="text-right py-2 px-3 font-semibold">{t('summary.testPacketsWeight', { weight: wLabel })}</th>
              <th className="text-right py-2 px-3 font-semibold">{t('summary.totalWeight', { weight: wLabel })}</th>
              <th className="text-right py-2 px-3 font-semibold">{t('summary.totalCostUsd')}</th>
            </tr>
          </thead>
          <tbody>
            {crops.map((crop) => {
              const demoWeight = (results.demoFarm.seedRequirementsKg[crop.id] ?? 0) * wf;
              const testWeight = (results.testPackets.seedRequirementsKg[crop.id] ?? 0) * wf;
              const totalWeight = demoWeight + testWeight;
              const totalUsd = (results.demoFarm.costsUsd[crop.id] ?? 0) + (results.testPackets.costsUsd[crop.id] ?? 0);

              return (
                <tr key={crop.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                  <td className="py-2 px-3 font-medium">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                        style={{ backgroundColor: crop.color }}
                      />
                      {crop.label}
                    </span>
                  </td>
                  <td className="text-right py-2 px-3 text-white/80">{formatNumber(demoWeight)}</td>
                  <td className="text-right py-2 px-3 text-white/80">{formatNumber(testWeight)}</td>
                  <td className="text-right py-2 px-3 font-semibold">{formatNumber(totalWeight)}</td>
                  <td className="text-right py-2 px-3 text-spi-orange-light">{formatCurrency(totalUsd, 'USD')}</td>
                </tr>
              );
            })}
            <tr className="font-bold text-sm bg-white/5">
              <td className="py-2.5 px-3">{t('summary.total')}</td>
              <td className="text-right py-2.5 px-3">
                {formatNumber(results.demoFarm.totalSeedKg * wf)}
              </td>
              <td className="text-right py-2.5 px-3">
                {formatNumber(results.testPackets.totalSeedKg * wf)}
              </td>
              <td className="text-right py-2.5 px-3">{formatNumber(results.combined.totalSeedKg * wf)}</td>
              <td className="text-right py-2.5 px-3 text-spi-orange">
                {formatCurrency(results.combined.totalCostUsd, 'USD')}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
          <div className="text-xl font-display font-bold text-white">
            {formatNumber(results.combined.totalSeedKg * wf)}
          </div>
          <div className="text-xs text-white/70 mt-0.5">{t('summary.totalSeedWeight', { weight: wLabel })}</div>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-lg p-3 text-center">
          <div className="text-xl font-display font-bold text-white">
            {formatCurrency(results.combined.totalCostLocal, currencyCode)}
          </div>
          <div className="text-xs text-white/70 mt-0.5">{t('summary.totalCostCurrency', { currency: currencyCode })}</div>
        </div>
        <div className="bg-spi-orange/20 backdrop-blur rounded-lg p-3 text-center border border-spi-orange/30">
          <div className="text-xl font-display font-bold text-spi-orange">
            {formatCurrency(results.combined.totalCostUsd, 'USD')}
          </div>
          <div className="text-xs text-white/70 mt-0.5">{t('summary.totalCostUsdLabel')}</div>
        </div>
      </div>
    </div>
  );
}
